import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { setSessionToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Loader2, ArrowLeft, Mail } from "lucide-react";
import api from "@/lib/api";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [loginToken, setLoginToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("login"); // login | otp | forgot | forgot-sent
  const [forgotEmail, setForgotEmail] = useState("");
  const { user, setUser, checkAuth } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && ["PLATFORM_ADMIN", "BACKOFFICE"].includes(user.role)) navigate("/admin");
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/staff-login", { email, password });
      if (res.data.requires_otp) {
        setLoginToken(res.data.login_token);
        setView("otp");
        toast.success("OTP sent to your staff email.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    }
    setLoading(false);
  };

  const verifyOtp = async () => {
    setLoading(true);
    try {
      const res = await api.post("/auth/staff-login/verify", { login_token: loginToken, code: otpCode });
      setSessionToken(res.data.session_token);
      setUser(res.data);
      await checkAuth();
      toast.success("Welcome to SetPlay Staff Console!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid OTP");
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setView("forgot-sent");
      toast.success("Password reset instructions sent!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reset email");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="admin-login-page">
      <div className="w-full max-w-sm mx-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-xl bg-slate-900 flex items-center justify-center mx-auto mb-3">
              <ShieldCheck className="w-7 h-7 text-white" />
            </div>
            <h1 className="font-heading text-xl font-bold uppercase tracking-tight">SetPlay Staff Login</h1>
            <p className="text-xs text-muted-foreground mt-1">
              {view === "forgot" ? "Reset Your Password" : view === "forgot-sent" ? "Check Your Email" : "Secure Staff Access"}
            </p>
          </div>

          {/* ── LOGIN FORM ── */}
          {view === "login" && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@setplay.ph" required data-testid="admin-email" />
              </div>
              <div>
                <Label className="text-xs">Password</Label>
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password" required data-testid="admin-password" />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11 rounded-full font-heading uppercase" data-testid="admin-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Staff OTP"}
              </Button>
              <button type="button" onClick={() => { setForgotEmail(email); setView("forgot"); }} className="w-full text-center text-xs text-muted-foreground hover:text-primary transition-colors mt-2" data-testid="staff-forgot-password-link">
                Forgot Password?
              </button>
            </form>
          )}

          {/* ── OTP STEP ── */}
          {view === "otp" && (
            <div className="space-y-4" data-testid="staff-otp-step">
              <div>
                <Label className="text-xs">Enter Email OTP</Label>
                <Input value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" data-testid="staff-otp-code" />
              </div>
              <Button type="button" onClick={verifyOtp} disabled={loading || otpCode.length < 6} className="w-full h-11 rounded-full font-heading uppercase" data-testid="verify-staff-otp-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify OTP"}
              </Button>
              <button type="button" onClick={() => { setView("login"); setOtpCode(""); setLoginToken(""); }} className="w-full text-center text-xs text-muted-foreground hover:text-primary transition-colors">
                <ArrowLeft className="w-3 h-3 inline mr-1" />Back to Login
              </button>
            </div>
          )}

          {/* ── FORGOT PASSWORD FORM ── */}
          {view === "forgot" && (
            <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="staff-forgot-password-form">
              <p className="text-sm text-muted-foreground">Enter your staff email address. We'll send you a link to reset your password.</p>
              <div>
                <Label className="text-xs">Staff Email</Label>
                <Input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="your.staff@setplay.ph" required data-testid="staff-forgot-email" />
              </div>
              <Button type="submit" disabled={loading || !forgotEmail} className="w-full h-11 rounded-full font-heading uppercase" data-testid="staff-forgot-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-2" />Send Reset Link</>}
              </Button>
              <button type="button" onClick={() => setView("login")} className="w-full text-center text-xs text-muted-foreground hover:text-primary transition-colors">
                <ArrowLeft className="w-3 h-3 inline mr-1" />Back to Login
              </button>
            </form>
          )}

          {/* ── FORGOT PASSWORD SENT ── */}
          {view === "forgot-sent" && (
            <div className="space-y-4 text-center" data-testid="staff-forgot-sent">
              <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mx-auto">
                <Mail className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-sm text-slate-700">If <strong>{forgotEmail}</strong> is registered, a password reset link has been sent to your inbox.</p>
              <p className="text-xs text-muted-foreground">The link expires in 1 hour. Check your spam folder if you don't see it.</p>
              <Button onClick={() => { setView("login"); setForgotEmail(""); }} className="w-full h-11 rounded-full font-heading uppercase" data-testid="staff-back-to-login">
                <ArrowLeft className="w-4 h-4 mr-2" />Back to Login
              </Button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-white/30 mt-6">Restricted Access</p>
      </div>
    </div>
  );
}
